import React, { useMemo } from 'react';
import type { WidgetSize } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import { getTradePnl, getTradeTimestampMs, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';

interface PerformanceByTimeOfDayWidgetProps {
  timeRange: 'week' | 'month' | 'year';
  size: WidgetSize;
  interval?: '1min' | '5min' | '10min' | '15min' | '30min' | '1h' | '4h';
  trades?: any[];
}

const pad = (value: number) => String(value).padStart(2, '0');

const getSlotLabel = (timestampMs: number, interval: NonNullable<PerformanceByTimeOfDayWidgetProps['interval']>, timeZone: string) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(timestampMs)).map((part) => [part.type, part.value]));
  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const rangeLabel = (startHour: number, startMinute: number, durationMinutes: number) => {
    const total = startHour * 60 + startMinute + durationMinutes;
    return `${pad(startHour)}:${pad(startMinute)} - ${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
  };

  switch (interval) {
    case '1min': {
      return rangeLabel(hour, minute, 1);
    }
    case '5min':
    case '10min':
    case '15min':
    case '30min': {
      const step = interval === '5min' ? 5 : interval === '10min' ? 10 : interval === '15min' ? 15 : 30;
      const startMinute = Math.floor(minute / step) * step;
      return rangeLabel(hour, startMinute, step);
    }
    case '1h':
      return rangeLabel(hour, 0, 60);
    case '4h': {
      const blockStart = Math.floor(hour / 4) * 4;
      return rangeLabel(blockStart, 0, 240);
    }
    default:
      return `${pad(hour)}:00 - ${pad(hour)}:59`;
  }
};

const buildTimeSlots = (interval: NonNullable<PerformanceByTimeOfDayWidgetProps['interval']>) => {
  const slots: string[] = [];
  switch (interval) {
    case '1min':
      for (let hour = 0; hour < 24; hour += 1) {
        for (let minute = 0; minute < 60; minute += 1) {
          const endMinute = minute + 1;
          const endHour = endMinute >= 60 ? (hour + 1) % 24 : hour;
          slots.push(`${pad(hour)}:${pad(minute)} - ${pad(endHour)}:${pad(endMinute >= 60 ? 0 : endMinute)}`);
        }
      }
      break;
    case '5min':
    case '10min':
    case '15min':
    case '30min': {
      const step = interval === '5min' ? 5 : interval === '10min' ? 10 : interval === '15min' ? 15 : 30;
      for (let hour = 0; hour < 24; hour += 1) {
        for (let minute = 0; minute < 60; minute += step) {
          const endMinute = minute + step;
          const endHour = endMinute >= 60 ? (hour + 1) % 24 : hour;
          slots.push(`${pad(hour)}:${pad(minute)} - ${pad(endHour)}:${pad(endMinute >= 60 ? 0 : endMinute)}`);
        }
      }
      break;
    }
    case '1h':
      for (let hour = 0; hour < 24; hour += 1) {
        slots.push(`${pad(hour)}:00 - ${pad((hour + 1) % 24)}:00`);
      }
      break;
    case '4h':
      slots.push('00:00 - 04:00', '04:00 - 08:00', '08:00 - 12:00', '12:00 - 16:00', '16:00 - 20:00', '20:00 - 00:00');
      break;
    default:
      for (let hour = 0; hour < 24; hour += 1) {
        slots.push(`${pad(hour)}:00 - ${pad(hour)}:59`);
      }
  }
  return slots;
};

export const PerformanceByTimeOfDayWidget: React.FC<PerformanceByTimeOfDayWidgetProps> = ({
  timeRange, 
  size,
  interval = '30min',
  trades = [],
}) => {
  const displayTimeZone = useDisplayTimezone();
  const perfData = useMemo(() => {
    const realizedTrades = trades.filter((trade) => normalizeTradeStatus(trade) === 'closed');
    if (!realizedTrades.length) return { items: [], maxPnl: 1 };

    const timeSlots = buildTimeSlots(interval);
    const data = new Map<string, { name: string; pnl: number; trades: number }>();
    timeSlots.forEach((slot) => data.set(slot, { name: slot, pnl: 0, trades: 0 }));

    realizedTrades.forEach((trade) => {
      const timestampMs = getTradeTimestampMs(trade);
      if (timestampMs == null) return;
      const slot = getSlotLabel(timestampMs, interval, displayTimeZone);
      const current = data.get(slot);
      if (!current) return;
      current.pnl += getTradePnl(trade);
      current.trades += 1;
    });

    const values = Array.from(data.values());
    const totalPnlVolume = values.reduce((acc, d) => acc + Math.abs(d.pnl), 0);
    const maxPnl = Math.max(1, ...values.map(d => Math.abs(d.pnl)));

    return {
      items: values.map(d => ({
        ...d,
        pct: totalPnlVolume > 0 ? (Math.abs(d.pnl) / totalPnlVolume) * 100 : 0,
      })),
      maxPnl,
    };
  }, [timeRange, interval, trades, displayTimeZone]);

  if (!perfData.items.length) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-zinc-200 text-sm text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
        Import Trades to see the widget
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto pr-2">
        <div className={`space-y-2.5 ${size === 'mini' ? 'space-y-1' : 'space-y-2.5'}`}>
          {perfData.items.map(item => (
            <div key={item.name} className="border-b border-zinc-200 dark:border-zinc-800 pb-2 last:border-b-0">
              <div className={`grid grid-cols-3 gap-4 items-center ${size === 'mini' ? 'text-xs' : 'text-sm'}`}>
                <span className="font-semibold">{item.name}</span>
                <div className="text-center">
                  <span className="text-zinc-600 dark:text-zinc-400 font-medium">
                    {item.trades} trades
                  </span>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <span className={`font-semibold ${item.pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {fmtMoney(item.pnl)}
                  </span>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 w-12">
                    {item.pct.toFixed(item.pct < 1 ? 2 : 1)}%
                  </span>
                </div>
              </div>
              {item.pnl !== 0 && (
                <div className="w-full bg-zinc-200 dark:bg-zinc-700/50 rounded-full h-1.5 mt-1 relative group cursor-pointer">
                  <div
                    className={`${item.pnl >= 0 ? 'bg-emerald-500' : 'bg-red-500'} h-1.5 rounded-full`}
                    style={{ width: `${(Math.abs(item.pnl) / perfData.maxPnl) * 100}%` }}
                  ></div>
                  <span
                    className="absolute left-0 rounded-full bg-zinc-400/25 opacity-0 group-hover:opacity-100 pointer-events-none transition"
                    style={{ width: `${(Math.abs(item.pnl) / perfData.maxPnl) * 100}%` }}
                  ></span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
